// @file Surface attachments store
import { conditionallyReplacePdfEmbedCode, memoizedArvoFetchLink } from '@@/bits/beethoven'
import { captureNonNetworkFetchError } from '@@/bits/error_tracker'
import { extractGifFirstFrame } from '@@/bits/image'
import { vSet } from '@@/bits/vue'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { WishApi } from '@@/surface/api/wish'
import { FetchError } from '@@/surface/api_fetch'
import type { AttachmentProps, Cid, Post } from '@@/types'
import type { JsonAPIResource, Link } from '@padlet/arvo'
import type { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { getDisplayAttributes as originalGetDisplayAttributes } from '@padlet/beethoven-client'
import { isEmpty } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useSurfaceAttachmentsStore = defineStore('surfaceAttachments', () => {
  const surfacePostsStore = useSurfacePostsStore()

  /* ---------------------- */
  /* STATE                  */
  /* ---------------------- */

  /**
   * A map of `Link` object by its URL.
   * The `Link` object is the serialization of the link record in the `links` table.
   */
  const linkAttributesByUrl = ref<Record<string, Link>>({})

  /**
   * A map of `BeethovenDisplayAttributes` by its URL.
   * This `BeethovenDisplayAttributes` object is a transformation of the `Link` object
   * through the `getDisplayAttributes` function from @padlet/beethoven-client.
   * However, we shouldn't store this data in Pinia because display attributes are
   * dependent on the post size. It should be up to the component to get the display
   * attributes suitable for its use case.
   */
  // const linkDisplayAttributes = ref<Record<string, BeethovenDisplayAttributes | null>>({})
  // We keep this commented out as context since we haven't fully migrated the store actions
  // related to this "display attributes" yet.

  const surfacePreviewImageLoaded = ref<Record<string, boolean>>({})
  const fileUploadingToPickerBox = ref<File | null>(null)

  /**
   * A map of first frame data URLs by GIF image URL.
   * Used to cache extracted first frames from GIFs to avoid redundant extractions.
   */
  const firstFrameUrlsByImageUrl = ref<Record<string, string>>({})

  /* ---------------------- */
  /* GETTER FUNCTIONS       */
  /* ---------------------- */

  const getLinkAttributesForUrl = (url: string): Link | null => {
    return linkAttributesByUrl.value[url] ?? null
  }

  const getLinkDisplayAttributesForUrl = (
    url: string,
    attachmentLink: Link | null = null,
  ): BeethovenDisplayAttributes | null => {
    if (attachmentLink != null) {
      return originalGetDisplayAttributes(attachmentLink, {})
    }
    // Get the link attributes instead of the display attributes.
    // This is one step towards removing reliance on storing display attributes in Pinia.
    // The return value is still converted to display attributes to be backward-compatible.
    const link = getLinkAttributesForUrl(url)
    if (link == null) return null
    const displayAttrs = originalGetDisplayAttributes(link, {})
    // Previously when we save `BeethovenDisplayAttributes` in Pinia, this `conditionallyReplacePdfEmbedCode`
    // would have already been called. But now we don't store display attributes in Pinia anymore ->  We must
    // call it here when we retrieve the display attributes to ensure the PDF embed code is correctly set.
    return conditionallyReplacePdfEmbedCode(displayAttrs)
  }

  const getLinkDisplayAttributesForPost = (postCid: string): BeethovenDisplayAttributes | null => {
    const post = surfacePostsStore.postEntitiesByCid[postCid]
    if (post == null) return null
    const attachmentUrl = post.wish_content?.attachment_props?.url ?? post.attachment
    return !isEmpty(attachmentUrl) ? getLinkDisplayAttributesForUrl(attachmentUrl) : null
  }

  const getAttachmentPropsFromPost = (postCid: string): AttachmentProps | null => {
    const post = surfacePostsStore.postEntitiesByCid[postCid]
    if (post == null) return null
    return post.wish_content?.attachment_props ?? null
  }

  const getFirstFrameUrlForImageUrl = async (imageUrl: string): Promise<string | null> => {
    if (imageUrl == null) return null
    // return cached first frame url if it exists
    if (firstFrameUrlsByImageUrl.value[imageUrl] != null) {
      return firstFrameUrlsByImageUrl.value[imageUrl]
    }

    // otherwise, extract the first frame and store it in the cache
    const firstFrameUrl = await extractGifFirstFrame(imageUrl)
    if (firstFrameUrl != null) {
      storeFirstFrameUrlForImageUrl(imageUrl, firstFrameUrl)
    }
    // return the first frame url
    return firstFrameUrlsByImageUrl.value[imageUrl] ?? null
  }

  const isSurfacePreviewImageLoadedForUrl = (url: string): boolean => {
    return surfacePreviewImageLoaded[url] ?? false
  }

  /* ---------------------- */
  /* ACTIONS                */
  /* ---------------------- */

  const fetchAndStoreLinkAttributes = async (url: string): Promise<Link | null> => {
    try {
      const link = await memoizedArvoFetchLink(url)
      const attributes = (link.data as JsonAPIResource<Link>).attributes
      storeLinkAttributes(url, attributes)
      return attributes
    } catch (error) {
      captureNonNetworkFetchError(error, { source: 'fetchAndStoreLinkAttributes' })
      return null
    }
  }
  const storeLinkAttributes = (url: string, attributes: Link): void => {
    vSet(linkAttributesByUrl.value, url, attributes)
  }

  const uploadFileToPickerBox = (file: File | null): void => {
    fileUploadingToPickerBox.value = file
  }
  const completeUploadingFileToPickerBox = (): void => {
    fileUploadingToPickerBox.value = null
  }

  /**
   * @deprecated Please use `fetchAndStoreLinkAttributes` instead.
   */
  const updateLinkDisplayAttributes = ({
    url,
    attachmentLink = null,
  }: {
    url: string
    attributes: BeethovenDisplayAttributes | null
    attachmentLink?: Link | null
  }): void => {
    // Don't store display attributes in Pinia anymore.
    // But trigger the fetch to update the link attributes.
    if (attachmentLink != null) {
      storeLinkAttributes(url, attachmentLink)
    } else {
      void fetchAndStoreLinkAttributes(url)
    }
  }

  const cancelUploadFileToPickerBox = (): void => {
    fileUploadingToPickerBox.value = null
  }

  const setSurfacePreviewImageLoaded = ({ url }: { url?: string }): void => {
    if (url != null) {
      surfacePreviewImageLoaded.value = {
        ...surfacePreviewImageLoaded.value,
        [url]: true,
      }
    }
  }

  const fetchDisplayAttributesForPost = async (postCid: Cid): Promise<void> => {
    const post = surfacePostsStore.postEntitiesByCid[postCid]
    if (post == null) return

    const attachmentUrl = post.wish_content?.attachment_props?.url ?? post.attachment
    if (isEmpty(attachmentUrl) || linkAttributesByUrl.value[attachmentUrl] != null) return

    if (post.attachment_link != null) {
      storeLinkAttributes(attachmentUrl, post.attachment_link)
    } else {
      await fetchAndStoreLinkAttributes(attachmentUrl)
    }
  }

  // method to fetch the user-specific uploaded attachment permalink and update the post
  const fetchAndUpdateAttachmentPermalinkForPost = async (post: Post): Promise<void> => {
    if (post.hashid == null || post.wish_content?.attachment_props?.signed_url == null) {
      return
    }
    try {
      const attachmentPermalink = await WishApi.fetchAttachmentPermalink(post.hashid)
      post.wish_content.attachment_props.hotlink = attachmentPermalink
    } catch (error) {
      // In case of error, we fallback to the signed attachment URL.
      post.wish_content.attachment_props.hotlink = post.wish_content.attachment_props.signed_url
      if (error instanceof FetchError && error.status === 422) {
        // We don't want to capture 422 errors because they are expected when the
        // backend is not updated with the attachment details yet.
        return
      }
      captureNonNetworkFetchError(error, { source: 'fetchAndUpdateAttachmentPermalinkForPost' })
    }
  }

  const storeFirstFrameUrlForImageUrl = (imageUrl: string, firstFrameUrl: string): void => {
    vSet(firstFrameUrlsByImageUrl.value, imageUrl, firstFrameUrl)
  }

  return {
    // state
    linkAttributesByUrl,
    firstFrameUrlsByImageUrl,
    surfacePreviewImageLoaded,
    fileUploadingToPickerBox,

    // getter functions
    getLinkAttributesForUrl,
    getLinkDisplayAttributesForUrl,
    getLinkDisplayAttributesForPost,
    getAttachmentPropsFromPost,
    getFirstFrameUrlForImageUrl,
    isSurfacePreviewImageLoadedForUrl,

    // actions
    fetchAndStoreLinkAttributes,
    uploadFileToPickerBox,
    cancelUploadFileToPickerBox,
    completeUploadingFileToPickerBox,
    updateLinkDisplayAttributes,
    storeFirstFrameUrlForImageUrl,
    setSurfacePreviewImageLoaded,
    fetchDisplayAttributesForPost,
    fetchAndUpdateAttachmentPermalinkForPost,
  }
})
